import {
  DocumentTemplateType,
  QueryFactory,
  ResourceType,
} from '@lmt/shared-types';
import { useTabId } from '@lmt/unicorn-shared/app/components/keep-alive/TabContext';
import {
  NavLink,
  TabType,
} from '@lmt/unicorn-shared/app/components/navigation/types';
import { getDocumentTitle } from '@lmt/unicorn-shared/app/components/patients/profile/cases/components/documents/common';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { getDefaultTabs } from '@lmt/unicorn-shared/app/features/navigation/selectors';
import {
  calendarSelectedTabIcon,
  calendarSettingsTabIcon,
  calendarTabIcon,
  customDictionarySelectedTabIcon,
  customDictionaryTabIcon,
  dashboardTabIcon,
  defaultSelectedTabIcon,
  defaultTabIcon,
  documentSelectedTabIcon,
  documentSettingsTabIcon,
  documentTabIcon,
  mappingsSelectedTabIcon,
  mappingsTabIcon,
  patientSelectedTabIcon,
  patientsSelectedTabIcon,
  patientsSettingsTabIcon,
  patientsTabIcon,
  patientTabIcon,
  settingsSelectedTabIcon,
  settingsTabIcon,
} from '@lmt/unicorn-shared/app/helpers/cached-images';
import { Location } from 'react-router';
import { snakeCase } from 'lodash';
import queryString from 'query-string';
import { useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { NavigateFunction } from 'react-router';
import { createId } from '../../helpers/create-id';
import { NS_DataBlockFields } from '../../helpers/localization/useScopedTranslation';
import i18next from 'i18next';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import {
  getState,
  GlobalState,
  UniDispatch,
  useUniDispatch,
  useUniSelector,
} from '../../store/types';
import { findTab, NavigationActions } from './reducer';
import { toast } from 'react-toastify';

const maxNumberOfTabs = 15;
const tabPath = (path: string, tabId: string) => `/tabs/${tabId}${path}`;

function openTab(
  path: string,
  dispatch: UniDispatch,
  navigate: NavigateFunction,
  search?: string,
) {
  const tab = createTabInBackground(path, dispatch, search);
  if (tab) {
    activateTab(tab, navigate);
  }
}

export function activateTab(tab: NavLink, navigate: NavigateFunction) {
  navigate({ pathname: tab.path, search: tab.search });
}

function getIcon(path: string) {
  if (
    pages.documentDesigner.matchPath(path) ||
    pages.createPatientDocument.matchPath(path)
  ) {
    return documentTabIcon;
  }
  if (pages.calendar.matchPath(path) || pages.calendarTab.matchPath(path)) {
    return calendarTabIcon;
  }
  if (pages.patientProfile.matchPath(path)) {
    return patientTabIcon;
  }
  if (pages.patients.matchPath(path) || pages.patientsTab.matchPath(path)) {
    return patientsTabIcon;
  }
}

const notDuplicatedPages = [
  pages.mergeAssistant,
  pages.tenantSettings,
  pages.subtenantSettings,
  pages.settings,
  pages.calendarVisitCardSettings,
  pages.calendarColorsSettings,
  pages.absences,
  pages.workstationSettings,
  // Custom UI
  pages.dataBlockFields,
  pages.dataBlockTypes,
  pages.mappings,
  pages.dataBlockSettings,
  // GDT
  pages.gdtMappings,
  pages.gdtMessages,
  pages.gdtOutgoingTemplates,
  pages.gdtTemplateRules,
  // HL7
  pages.hl7Filters,
  pages.hl7Mappings,
  pages.hl7Messages,
  pages.hl7MessageTypes,
  pages.hl7OutgoingTemplates,
  pages.hl7TemplateRules,
  // Dictionaries
  pages.doctors,
  pages.attendingDoctors,
  pages.dictionaryTags,
  pages.dictionaryVisitTypes,
  // Users
  pages.users,
  pages.roles,
  pages.subtenants,
];

function extractSearchFromPath(
  path: string,
  search?: string,
): { path: string; search: string | undefined } {
  if (path.includes('?')) {
    const searchPart = path.substring(path.indexOf('?') + 1);
    search = (search ?? '') + (search ? '&' : '') + searchPart;
    path = path.substring(0, path.indexOf('?'));
  }
  return { path, search };
}

export function createTabInBackground(
  path: string,
  dispatch: UniDispatch,
  search?: string,
): NavLink | null {
  ({ path, search } = extractSearchFromPath(path, search));
  const tabs = getState().navigation.tabs;
  const pathWithTabPrefix = '/tabs/123' + path;
  // Open tab if it exists instead of creating a new one
  for (const page of notDuplicatedPages) {
    if (page.matchPath(pathWithTabPrefix)) {
      const existingTab = tabs.find((tab) => page.matchPath(tab.path));
      if (existingTab?.id) {
        return existingTab;
      }
    }
  }

  // Open resource page
  const resourceId =
    pages.resources.matchPath(pathWithTabPrefix)?.params?.resourceType;
  if (resourceId !== undefined) {
    const resourcePage = pages.resources.link({
      resourceType: resourceId,
    });
    const existingTab = tabs.find((tab) => tab.path.includes(resourcePage));
    if (existingTab?.id) {
      return existingTab;
    }
  }

  // Open custom dictionary if it's opened
  const customDictionaryId =
    pages.customDictionary.matchPath(pathWithTabPrefix)?.params?.dictionaryId;
  if (customDictionaryId) {
    const dictionaryPage = pages.customDictionary.link({
      dictionaryId: customDictionaryId,
    });
    const existingTab = tabs.find((tab) => tab.path.includes(dictionaryPage));
    if (existingTab?.id) {
      return existingTab;
    }
  }

  const patientProfileId =
    pages.patientProfile.matchPath(pathWithTabPrefix)?.params?.patientId;
  // Jump to patient's tab if it's opened
  if (patientProfileId) {
    const patientProfilePage = pages.patientProfile.link({
      patientId: patientProfileId,
    });
    // we ensure that patientProfilePage is followed by '/' or '?' or $
    // to prevent '/patient/1' matching the '/patient/10' tab url
    // Bug 40869: Wrong patient is opened
    const regex = new RegExp(patientProfilePage + '[$\\?/]');
    const existingTab = tabs.find((tab) => tab.path.match(regex));
    if (existingTab && existingTab.id) {
      if (path.length > patientProfilePage.length) {
        const newPath = tabPath(path, existingTab.id);
        dispatch(
          NavigationActions.pathChanged({
            id: existingTab.id,
            path: newPath,
            search: search,
          }),
        );
        return {
          ...existingTab,
          path: newPath,
          search: search,
        };
      }
      return existingTab;
    }
  }

  if (tabs.length === maxNumberOfTabs) {
    toast(i18next.t('tabs.menu.max_amount_of_tabs_reached'), {
      type: 'warning',
    });
    return null;
  }
  // Create a new tab
  const id = createId();
  const tab = {
    title: '',
    path: tabPath(path, id),
    permission: true,
    id: id,
    search: search,
    icon: getIcon(path),
    type: TabType.Settings,
    openedFromId: getState().navigation.lastActiveTabId ?? undefined,
  };
  dispatch(NavigationActions.tabCreated(tab));
  return tab;
}

function changeTabSearch(
  tabId: string | undefined,
  query: string,
  dispatch: UniDispatch,
  navigate: NavigateFunction,
  location: Location,
) {
  const currentlyOpenedTabId = getTabId(location);
  if (tabId) {
    dispatch(
      NavigationActions.queryStringChanged({ id: tabId, search: query }),
    );
    if (currentlyOpenedTabId === tabId) {
      navigate({ pathname: location.pathname, search: query });
    }
  } else {
    // handling default Calendar and Patient pages
    if (!currentlyOpenedTabId && !tabId) {
      const title =
        location.pathname === '/calendar'
          ? 'Calendar'
          : location.pathname === '/patients'
            ? 'Patients'
            : '';
      dispatch(
        NavigationActions.defaultTabQueryStringChanged({
          title: title,
          search: query,
        }),
      );
      navigate({ search: query });
    }
  }
}

export function getCurrentUrl() {
  let hash = window.document.location.hash.substring(1); // remove leading hash
  // it's possible to have hash like https://127.0.0.1:3149/#/tabs/_1oicxhqcv/patients/142015/cases/74327#caseItemvisit_9699
  // we need to take the `/tabs/_1oicxhqcv/patients/142015/cases/74327` part
  const hashbangPosition = hash.indexOf('#');
  if (hashbangPosition !== -1) {
    hash = hash.substring(0, hashbangPosition);
  }
  if (hash.startsWith('/')) return hash;
  return window.document.location.pathname;
}

export function getCurrentlyOpenedTabId() {
  return getTabIdFromUrl(getCurrentUrl());
}

export function getTabId(location: Location) {
  return getTabIdFromUrl(location.pathname);
}

export function getTabIdFromUrl(path: string): string {
  const match = pages.tabs.matchPath(path);
  const tabId = match?.params?.tabId;
  if (tabId) return tabId;

  return getTabIdFromNonTabPage(path);
}

export function getTabIdFromNonTabPage(path: string): string {
  const tabName = pages.nonTabPage.matchPath(path)?.params?.tabName!;

  // On Desktop 'fixed' tabs are returned including query string (e.g. 'calendar?configurationId=123...')
  // So, we need to take the part before the question mark
  if (tabName?.includes('?')) return tabName.substring(0, tabName.indexOf('?'));
  return tabName;
}

export function useTabs() {
  const tabId = useTabId();
  const dispatch = useUniDispatch();
  const modals = useModal();
  const navigate = useNavigate();
  const location = useLocation();
  const defaultTabs = useUniSelector(getDefaultTabs).map((x) => x.id ?? '');
  const navigateRef = useRef(navigate);
  useLayoutEffect(() => {
    navigateRef.current = navigate;
  });

  return useMemo(
    () => ({
      changeTabPath(pagePath: string, replace = false, inBackground = false) {
        changeTabPath(
          tabId,
          pagePath,
          dispatch,
          navigateRef.current,
          location,
          replace,
          inBackground,
        );
      },
      changeTabSearch(query: string) {
        changeTabSearch(tabId, query, dispatch, navigateRef.current, location);
      },
      openTab(path: string, search?: string) {
        openTab(path, dispatch, navigateRef.current, search);
      },
      createTabInBackground(path: string, search?: string) {
        createTabInBackground(path, dispatch, search);
      },
      tabId: tabId,
      // fixed means Tab to the left of '|' in NavTab (i.e. tabs without '/tabs/_bms9ikxsu' in URL)
      isFixedTab:
        !tabId || defaultTabs.includes(tabId) || !tabId.startsWith('_'),
    }),
    [],
  );
}

function changeTabPath(
  tabId: string | undefined,
  pagePath: string,
  dispatch: UniDispatch,
  navigate: NavigateFunction,
  location: Location,
  replace = false,
  inBackground = false,
) {
  if (!tabId) return;

  const currentlyOpenedTabId = getTabId(location);
  const { path, search } = extractSearchFromPath(tabPath(pagePath, tabId));
  if (inBackground) {
    dispatch(
      NavigationActions.queryStringChanged({
        id: tabId,
        path: path,
        search: search,
      }),
    );
    return;
  }
  dispatch(
    NavigationActions.pathChanged({
      id: tabId,
      path: path,
    }),
  );

  if (currentlyOpenedTabId === tabId) {
    if (replace) {
      navigate({ pathname: path, search }, { replace: true });
    } else {
      navigate({ pathname: path, search });
    }
  }
}

export function removeTab(
  tabId: string,
  isCurrent: boolean,
  navigate: NavigateFunction,
) {
  return (dispatch: UniDispatch, getState: () => GlobalState) => {
    const tabs = getState().navigation.tabs;
    if (isCurrent) {
      const currentId = tabs.findIndex((tab) => tab.id === tabId);
      const openedFrom = !!tabs[currentId].openedFromId
        ? findTab(getState().navigation, tabs[currentId].openedFromId)
        : undefined;
      if (tabs.length === 1) {
        const defaultTabs = getState().navigation.defaultTabs;
        const patientTab = defaultTabs[defaultTabs.length - 1];
        const switchTo = openedFrom ?? patientTab;
        navigate(
          { pathname: switchTo.path, search: switchTo.search },
          { replace: true },
        );
      } else {
        const tabNextToClosedIndex = currentId === 0 ? 1 : currentId - 1;
        const tab = openedFrom ?? tabs[tabNextToClosedIndex];
        navigate({ pathname: tab.path, search: tab.search }, { replace: true });
      }
    }
    dispatch(NavigationActions.tabRemoved(tabId));
    const { tabStateChangeSubscribers } = getState().navigation;
    tabStateChangeSubscribers.forEach((subscriber) =>
      subscriber?.(tabId, 'removed'),
    );
  };
}

export function getPageName(path: string) {
  const pageName = path.split('/')[1];
  if (path.startsWith('/hl7/')) {
    const subPageName = path.split('/')[2];
    return `hl7.${subPageName}`;
  }
  if (path.startsWith('/gdt/')) {
    const subPageName = path.split('/')[2];
    return `gdt.${subPageName}`;
  }
  if (path.startsWith('/statistics')) {
    const subPageName = path.split('/')[2];
    return `statistics.${subPageName ?? 'viewer'}`;
  }
  return pageName;
}

function getPatientIdFromPath(path: string): number | undefined | null {
  const patientId =
    pages.patientProfile.matchPath(path)?.params?.patientId ||
    pages.createPatientDocument.matchPath(path)?.params?.patientId;

  return patientId;
}

function getDocumentIdFromPath(path: string): string | undefined {
  return undefined;
  // if ( matchPath(pages2.documentDesigner.route, path)) {
  //
  // }
  // return undefined;
  //   ?.params?.templateId;
  // return documentId;
}

function getDocumentTemplateIdFromPath(
  path: string,
): number | undefined | null {
  const templateId =
    pages.documentDesigner.matchPath(path)?.params?.templateId ||
    pages.gdtOutgoingTemplatesWithId.matchPath(path)?.params?.templateId ||
    pages.createPatientDocument.matchPath(path)?.params?.templateId;

  return templateId;
}

function getDocumentTemplateTypeFromPath(
  path: string,
): DocumentTemplateType | undefined | null {
  const templateId =
    pages.documentDesigner.matchPath(path)?.params?.templateType ||
    pages.documentDesigner.matchPath(path)?.params?.templateType;
  return templateId;
}

function getCalendarConfigurationId(search: string) {
  const parsedResult = queryString.parse(search, {
    parseNumbers: true,
  }) as any;
  return parsedResult?.configurationId;
}

export function useTitleAndIconForTab(
  path: string,
  search?: string,
  useDefaultTitle?: boolean,
) {
  const result = useInnerTitleAndIconForTab(path, search, useDefaultTitle);
  if (typeof result.title !== 'string') {
    console.error(
      'useTitleAndIconForTab: Returned object instead of Title',
      result.title,
      path,
      search,
    );
  }
  return result;
}

function useInnerTitleAndIconForTab(
  path: string,
  search?: string,
  useDefaultTitle?: boolean,
): {
  title: string;
  type: TabType;
  icon?: React.FC<{ className?: string }>;
  selectedIcon?: string | any;
} {
  const i18next = useTranslation();
  const i18nextDict = useTranslation(NS_DataBlockFields);
  const pageName =
    pages.tabs.matchPath(path)?.params?.tabName ??
    pages.nonTabPage.matchPath(path)?.params?.tabName;

  const patientId = getPatientIdFromPath(path);
  const patientQuery = QueryFactory.PatientQuery.useGetPatientQuery(
    patientId!,
    {
      enabled: !!patientId,
      throwOnError: false,
    },
  );

  const documentTemplateId = getDocumentTemplateIdFromPath(path);
  const documentTemplateQuery = QueryFactory.DocumentTemplateQuery.useGetQuery({
    enabled: !!documentTemplateId,
    throwOnError: false,
  });
  const documentTemplate = documentTemplateQuery.data?.find(
    (x) => x.id === documentTemplateId,
  );
  const documentTemplateTitle = documentTemplate
    ? documentTemplate.isDefault
      ? i18next.t(documentTemplate.title as any)
      : documentTemplate.title
    : undefined;

  const hl7Match = pages.hl7.matchPath(path);
  const gdtMatch = pages.gdt.matchPath(path);

  const gdtTemplateQuery = QueryFactory.GDTOutgoingTemplatesQuery.useGetQuery({
    enabled: !!documentTemplateId && !!gdtMatch,
    throwOnError: false,
  });
  const hl7TemplateQuery = QueryFactory.HL7OutgoingTemplatesQuery.useGetQuery({
    enabled: !!documentTemplateId && !!hl7Match,
    throwOnError: false,
  });
  const gdtTemplate = gdtTemplateQuery.data?.find(
    (x) => x.id === documentTemplateId,
  );
  const hl7Template = hl7TemplateQuery.data?.find(
    (x) => x.id === documentTemplateId,
  );
  const gdtTemplateTitle = gdtTemplate
    ? gdtTemplate.isDefault
      ? i18next.t(gdtTemplate.title as any)
      : gdtTemplate.title
    : undefined;

  const hl7TemplateTitle = hl7Template
    ? hl7Template.isDefault
      ? i18next.t(hl7Template.title as any)
      : hl7Template.title
    : undefined;

  const documentId = getDocumentIdFromPath(path);
  const documentInfo = QueryFactory.FileQuery.useGetFileInfoQuery(documentId!, {
    enabled: !!documentId,
    throwOnError: false,
  });
  const documentTitle = documentInfo.data?.name;

  const dictionaryMatch = pages.dictionary.matchPath(path);

  const customDictionaryId =
    pages.customDictionary.matchPath(path)?.params?.dictionaryId;
  const customDictionariesQuery =
    QueryFactory.DataBlockDictionaryQuery.useGetQuery({
      enabled: !!customDictionaryId,
      throwOnError: false,
    });
  const customDictionary = useMemo(
    () =>
      customDictionariesQuery.data?.find((x) => x.id === customDictionaryId),
    [customDictionariesQuery.data],
  );

  const calendarConfigurationId =
    !useDefaultTitle && search ? getCalendarConfigurationId(search) : undefined;
  const calendarConfigurationQuery =
    QueryFactory.CalendarConfigurationQuery.useGetCalendarConfigurationQuery(
      calendarConfigurationId,
      {
        enabled: !!calendarConfigurationId,
        throwOnError: false,
      },
    );

  const defaultTitle = String(
    i18next.t(`tabs.${snakeCase(pageName).replace('-', '_')}` as any),
  );
  if (
    pages.patients.matchPath(path) ||
    pages.patientsTab.matchPath(path) ||
    pages.patientProfile.matchPath(path)
  ) {
    return patientId
      ? patientQuery.isLoading || !patientQuery.data
        ? {
            title: i18next.t('tabs.unknown'),
            type: TabType.Patient,
          }
        : {
            icon: patientTabIcon,
            selectedIcon: patientSelectedTabIcon,
            type: TabType.Patient,
            title: `${patientQuery.data.lastName} ${patientQuery.data.firstName}`,
          }
      : {
          icon: patientsTabIcon,
          selectedIcon: patientsSelectedTabIcon,
          title: i18next.t('tabs.patients'),
          type: TabType.Patient,
        };
  } else if (pages.audit.matchPath(path)) {
    return {
      icon: defaultTabIcon,
      selectedIcon: defaultSelectedTabIcon,
      title: i18next.t('tabs.audit.title'),
      type: TabType.Settings,
    };
  } else if (hl7Match) {
    return {
      icon: mappingsTabIcon,
      selectedIcon: mappingsSelectedTabIcon,
      title:
        i18next.t(
          `tabs.hl7.${snakeCase(hl7Match.params.subPage).replace(
            '-',
            '_',
          )}.title` as any,
        ) + (hl7TemplateTitle ? ` - ${hl7TemplateTitle}` : ''),
      type: TabType.Settings,
    };
  } else if (gdtMatch) {
    if (gdtTemplateTitle || pages.gdtOutgoingTemplates.matchPath(path)) {
      return {
        icon: documentSettingsTabIcon,
        selectedIcon: documentSelectedTabIcon,
        type: TabType.Settings,
        title:
          i18next.t(
            `tabs.gdt.${snakeCase(gdtMatch.params.subPage).replace(
              '-',
              '_',
            )}.title` as any,
          ) + (gdtTemplateTitle ? ` - ${gdtTemplateTitle}` : ''),
      };
    }
    return {
      icon: mappingsTabIcon,
      selectedIcon: mappingsSelectedTabIcon,
      type: TabType.Settings,
      title: i18next.t(
        `tabs.gdt.${snakeCase(gdtMatch.params.subPage).replace(
          '-',
          '_',
        )}.title` as any,
      ),
    };
  } else if (pages.dashboard.matchPath(path)) {
    return {
      icon: dashboardTabIcon,
      selectedIcon: calendarSelectedTabIcon,
      title: i18next.t('tabs.dashboard'),
      type: TabType.Dashboard,
    };
  } else if (pages.statisticsDesigner.matchPath(path)) {
    return {
      icon: mappingsTabIcon,
      selectedIcon: mappingsSelectedTabIcon,
      title: i18next.t('tabs.statistics.designer.title'),
      type: TabType.Settings,
    };
  } else if (pages.statisticsViewer.matchPath(path)) {
    return {
      icon: mappingsTabIcon,
      selectedIcon: mappingsSelectedTabIcon,
      title: i18next.t('tabs.statistics.viewer.title'),
      type: TabType.Settings,
    };
  } else if (dictionaryMatch) {
    return {
      icon: mappingsTabIcon,
      selectedIcon: mappingsSelectedTabIcon,
      title: i18nextDict.t(
        `dictionaries.${snakeCase(
          dictionaryMatch.params.dictionaryType,
        ).replace('-', '_')}.title` as any,
      ),
      type: TabType.Settings,
    };
  } else if (pages.createPatientDocument.matchPath(path)) {
    if (patientQuery.data) {
      if (documentTemplateTitle || documentTitle) {
        const title = documentTitle
          ? getDocumentTitle(documentTitle)
          : documentTemplateTitle;
        return {
          icon: documentTabIcon,
          selectedIcon: documentSelectedTabIcon,
          type: TabType.Patient,
          title: `${patientQuery.data.lastName} ${patientQuery.data.firstName} - ${title}`,
        };
      } else {
        return {
          icon: documentTabIcon,
          selectedIcon: documentSelectedTabIcon,
          type: TabType.Patient,
          title: `${patientQuery.data.lastName} ${patientQuery.data.firstName}`,
        };
      }
    }
    return {
      icon: documentSettingsTabIcon,
      selectedIcon: documentSelectedTabIcon,
      title: i18next.t('tabs.documents.title'),
      type: TabType.Settings,
    };
  } else if (pages.document.matchPath(path)) {
    let documentTypeTitle = i18next.t('tabs.documents.title');

    const templateType = getDocumentTemplateTypeFromPath(path);
    if (templateType === DocumentTemplateType.DocumentHeader) {
      documentTypeTitle = i18next.t('tabs.document_headers.title');
    } else if (templateType === DocumentTemplateType.DocumentFooter) {
      documentTypeTitle = i18next.t('tabs.document_footers.title');
    }
    return {
      icon: documentSettingsTabIcon,
      selectedIcon: documentSelectedTabIcon,
      title:
        documentTypeTitle +
        (documentTemplateTitle ? ' - ' + documentTemplateTitle : ''),
      type: TabType.Settings,
    };
  } else if (
    pages.calendarTab.matchPath(path) ||
    pages.calendar.matchPath(path)
  ) {
    if (pages.calendarColorsSettings.matchPath(path)) {
      return {
        icon: calendarSettingsTabIcon,
        selectedIcon: calendarSelectedTabIcon,
        title: i18next.t(`tabs.calendar_color_settings`),
        type: TabType.Settings,
      };
    } else if (pages.calendarVisitCardSettings.matchPath(path)) {
      return {
        icon: calendarSettingsTabIcon,
        selectedIcon: calendarSelectedTabIcon,
        title: i18next.t(`tabs.calendar_visit_card_settings`),
        type: TabType.Settings,
      };
    } else if (!useDefaultTitle) {
      const configuration = calendarConfigurationQuery.data;
      return {
        icon: calendarTabIcon,
        selectedIcon: calendarSelectedTabIcon,
        title: configuration?.isSystem
          ? i18next.t(configuration.name as any)
          : (configuration?.name ?? ''),
        type: TabType.Calendar,
      };
    }
    return {
      icon: calendarTabIcon,
      selectedIcon: calendarSelectedTabIcon,
      title: defaultTitle,
      type: TabType.Calendar,
    };
  } else if (customDictionary) {
    return {
      icon: customDictionaryTabIcon,
      selectedIcon: customDictionarySelectedTabIcon,
      title: i18nextDict.t(customDictionary.title as any),
      type: TabType.Settings,
    };
  } else if (pages.resources.matchPath(path)) {
    const matchedPath = pages.resources.matchPath(path)!;
    let title = defaultTitle;
    if (matchedPath.params.resourceType === ResourceType.Doctors)
      title = i18next.t(`resources.doctors.title`);
    if (matchedPath.params.resourceType === ResourceType.SpecialConditions)
      title = i18next.t(`resources.special_conditions.title`);
    if (matchedPath.params.resourceType === ResourceType.Places)
      title = i18next.t(`resources.place.title`);

    return {
      icon: defaultTabIcon,
      selectedIcon: defaultSelectedTabIcon,
      title: title,
      type: TabType.Settings,
    };
  } else if (pages.settings.matchPath(path)) {
    return {
      icon: settingsTabIcon,
      selectedIcon: settingsSelectedTabIcon,
      title: defaultTitle,
      type: TabType.Settings,
    };
  } else if (pages.mappings.matchPath(path)) {
    return {
      icon: mappingsTabIcon,
      selectedIcon: mappingsSelectedTabIcon,
      title: i18next.t('tabs.data_blocks.mappings.title'),
      type: TabType.Settings,
    };
  } else if (pages.dataBlockFields.matchPath(path)) {
    return {
      icon: defaultTabIcon,
      selectedIcon: defaultSelectedTabIcon,
      title: i18next.t('tabs.data_blocks.fields.title'),
      type: TabType.Settings,
    };
  } else if (pages.dataBlockTypes.matchPath(path)) {
    return {
      icon: defaultTabIcon,
      selectedIcon: defaultSelectedTabIcon,
      title: i18next.t('tabs.data_blocks.types.title'),
      type: TabType.Settings,
    };
  } else if (pages.dataBlockSettings.matchPath(path)) {
    return {
      icon: defaultTabIcon,
      selectedIcon: defaultSelectedTabIcon,
      title: i18next.t('tabs.data_blocks.settings.title'),
      type: TabType.Settings,
    };
  } else if (pages.users.matchPath(path)) {
    return {
      icon: defaultTabIcon,
      selectedIcon: defaultSelectedTabIcon,
      title: i18next.t('tabs.users_section.users.title'),
      type: TabType.Settings,
    };
  } else if (pages.roles.matchPath(path)) {
    return {
      icon: defaultTabIcon,
      selectedIcon: defaultSelectedTabIcon,
      title: i18next.t('tabs.users_section.roles.title'),
      type: TabType.Settings,
    };
  } else if (pages.subtenants.matchPath(path)) {
    return {
      icon: defaultTabIcon,
      selectedIcon: defaultSelectedTabIcon,
      title: i18next.t('tabs.users_section.subtenants.title'),
      type: TabType.Settings,
    };
  } else if (pages.mergeAssistant.matchPath(path)) {
    return {
      icon: patientsSettingsTabIcon,
      selectedIcon: patientsSelectedTabIcon,
      title: i18next.t('tabs.merge_assistant.title'),
      type: TabType.Settings,
    };
  } else if (pages.tenantSettings.matchPath(path)) {
    return {
      icon: settingsTabIcon,
      selectedIcon: settingsSelectedTabIcon,
      title: i18next.t('tabs.tenant_settings.title'),
      type: TabType.Settings,
    };
  } else if (pages.subtenantSettings.matchPath(path)) {
    return {
      icon: settingsTabIcon,
      selectedIcon: settingsSelectedTabIcon,
      title: i18next.t('tabs.subtenant_settings.title'),
      type: TabType.Settings,
    };
  } else if (pages.workstationSettings.matchPath(path)) {
    return {
      icon: settingsTabIcon,
      selectedIcon: settingsSelectedTabIcon,
      title: i18next.t('tabs.workstation_settings.title'),
      type: TabType.Settings,
    };
  } else if (pages.documentTemplateRules.matchPath(path)) {
    return {
      icon: mappingsTabIcon,
      selectedIcon: mappingsSelectedTabIcon,
      title: i18next.t('tabs.document_template_rules.title'),
      type: TabType.Settings,
    };
  } else {
    return {
      icon: defaultTabIcon,
      selectedIcon: defaultSelectedTabIcon,
      title: String(defaultTitle),
      type: TabType.Settings,
    };
  }
}
